import React, { useState, useEffect, useRef } from "react";
import { Button, Tag } from "../../ui";
import { useNavigate, useLocation } from "react-router-dom";
import { MDBContainer } from "mdb-react-ui-kit";
import NativeInput from "../../NativeInput";
import { toast } from "react-toastify";
import { api } from "../../../services";
import { useTranslation } from "react-i18next";

function AdminEditApp() {
  const { t } = useTranslation("translation");
  const [form, setForm] = useState({
    id: "",
    name: "",
    description: "",
    arabicName: "",
    arabicDescription: "",
    logo: "",
    apiUrl: "",
    integrationMethod: "API_KEY",
    integrationType: "DIRECT",
    status: "Pending",
    credentialsTemplate: "",
    providerKey: "",
    webhookToken: "",
    appId: "",
    authorizationUrl: "",
    allowedUser: [],
  });

  const [appType, setAppType] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isOther = appType === "OTHER";

  // Fetch users on component mount
  useEffect(() => {
    // Check if users were passed from parent component
    if (location.state?.users && location.state.users.length > 0) {
      setUsers(location.state.users);
      setUsersFetched(true);
    } else {
      // Fetch only if not provided
      fetchUsers();
    }
  }, [location.state]);

  // Load app data from location state
  useEffect(() => {
    if (location.state?.app) {
      const app = location.state.app;
      setForm({
        id: app._id,
        name: app.name || "",
        description: app.description || "",
        arabicName: app.name_ar || "",
        arabicDescription: app.description_ar || "",
        logo: app.logo || "",
        apiUrl: isOther ? "https://dummy-api.com" : app.apiUrl || "",
        integrationMethod: isOther
          ? "NONE"
          : app.integrationMethod || "API_KEY",
        integrationType: isOther ? "DIRECT" : app.integrationType || "DIRECT",
        status: app.status || (isOther ? "Pending" : "Active"),
        credentialsTemplate: app.credentialsTemplate
          ? JSON.stringify(app.credentialsTemplate, null, 2)
          : "",
        providerKey: app.providerKey || "",
        webhookToken: app.webhookToken || "",
        appId: app.appId || "",
        authorizationUrl: app.authorizationUrl || "",
        allowedUser: app.allowedUsers || [], // Load existing allowed users
      });
      setAppType(app.appType || "");
    } else {
      navigate("/admin/available-apps");
    }
  }, [location, navigate, isOther]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.multi-select-container')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position
  useEffect(() => {
    if (dropdownOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 250;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [dropdownOpen]);

  const fetchUsers = async () => {
    if (usersFetched) return;
    
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem("adminToken");
      const response = await api.get("/admin/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users || []);
      setUsersFetched(true);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(`${t("Failed to fetch users")}`);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      let updated = { ...prev, [name]: value };
      
      // Clear allowed users if status changes from QA Testing
      if (name === "status" && value !== "QA Testing") {
        updated.allowedUser = [];
      }
      
      return updated;
    });
  };

  const handleUserToggle = (userId) => {
    setForm(prev => ({
      ...prev,
      allowedUser: prev.allowedUser.includes(userId)
        ? prev.allowedUser.filter(id => id !== userId)
        : [...prev.allowedUser, userId]
    }));
  };

  const handleSelectAll = () => {
    if (form.allowedUser.length === filteredUsers.length) {
      setForm(prev => ({ ...prev, allowedUser: [] }));
    } else {
      const allUserIds = filteredUsers.map(user => user._id || user.id);
      setForm(prev => ({ ...prev, allowedUser: allUserIds }));
    }
  };

  const validateAndParseJSON = (jsonString) => {
    try {
      return jsonString ? JSON.parse(jsonString) : null;
    } catch {
      return null;
    }
  };

  const filteredUsers = users.filter(user => {
    const userName = user.name || user.username || user.email || '';
    return userName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getUserDisplay = (user) => {
    const displayName = user.name || user.username || user.email;
    const emailSuffix = user.email && user.name ? ` (${user.email})` : '';
    return `${displayName}${emailSuffix}`;
  };

  const getArabicFieldError = (value, fieldName) => {
     if (!value) return `${fieldName} ${t("is required")}`;
  
     // Only English letters are disallowed, numbers and special characters are allowed
     if (/[a-zA-Z]/.test(value)) return `${fieldName} ${t("must not contain English letters")}`;
     if (value.length > 160) return `${fieldName} ${t("must not exceed 160 characters")}`;
  return null;
  };

  const handleSubmit = async () => {
    // Validate QA Testing status
    if (form.status === "QA Testing" && form.allowedUser.length === 0) {
      toast.error(`${t("Please select at least one user for QA Testing")}`);
      return;
    }

    // Validate Arabic fields for all app types
    const arabicNameError = getArabicFieldError(form.arabicName, t("Arabic Name"));
    if (arabicNameError) {
      toast.error(arabicNameError);
      return;
    }

    const arabicDescError = getArabicFieldError(form.arabicDescription, t("Arabic Description"));
    if (arabicDescError) {
      toast.error(arabicDescError);
      return;
    }

    if (isOther) {
      // For OTHER apps, only validate name is present
      if (!form.name) {
        toast.error(`${t("Name is required for Other apps")}`);
        return;
      }
    } else {
      // For specific app types, validate all required fields
      const requiredFields = [
        "name",
        "apiUrl",
        "integrationMethod",
        "integrationType",
        "status",
      ];

      if (appType === "CHEFZ") {
        requiredFields.push("providerKey", "webhookToken");
      } else if (appType === "KEETA") {
        requiredFields.push("appId", "authorizationUrl");
      }

      const missingFields = requiredFields.filter((field) => !form[field]);
      if (missingFields.length > 0) {
        toast.error(
          `${t("Missing required fields:")} ${missingFields.join(", ")}`
        );
        return;
      }

      const parsedCredentialsTemplate = validateAndParseJSON(
        form.credentialsTemplate
      );
      if (form.credentialsTemplate && !parsedCredentialsTemplate) {
        toast.error(`${t("Invalid JSON format for Credentials Template.")}`);
        return;
      }
    }

    try {
      const token = localStorage.getItem("adminToken");

      const payload = {
        ...form,
        name_ar: form.arabicName,
        description_ar: form.arabicDescription,
        credentialsTemplate: isOther
          ? null
          : validateAndParseJSON(form.credentialsTemplate),
        appType: appType,
        ...(form.status === "QA Testing" && { allowedUsers: form.allowedUser }),
        ...(isOther && {
          apiUrl: "https://dummy-api.com",
          integrationMethod: "NONE",
          integrationType: "DIRECT",
          providerKey: "DUMMY_PROVIDER",
          webhookToken: "DUMMY_TOKEN",
          appId: "DUMMY_APP_ID",
          authorizationUrl: "https://dummy-auth.com",
        }),
      };

      // Remove allowedUser field if status is not QA Testing
      if (form.status !== "QA Testing") {
        delete payload.allowedUser;
      }

      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined && v !== "")
      );

      await api.post(`/admin/available-apps`, filteredPayload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(`${t("App updated successfully!")}`);
      setTimeout(() => {
        navigate("/admin/available-apps");
      }, 2000);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update app.";
      toast.error(`${t("error")}: ${message}`);
    }
  };

  const dropdownStyles = {
    position: 'absolute',
    width: '100%',
    backgroundColor: 'white',
    border: '1px solid #dee2e6',
    borderRadius: '0.375rem',
    boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
    zIndex: 1050,
    maxHeight: '250px',
    overflowY: 'auto',
    overflowX: 'hidden',
    ...(dropdownPosition === 'top' ? {
      bottom: '100%',
      marginBottom: '0.25rem',
      borderBottomLeftRadius: '0.375rem',
      borderBottomRightRadius: '0.375rem',
    } : {
      top: '100%',
      marginTop: '0.25rem',
      borderTopLeftRadius: '0.375rem',
      borderTopRightRadius: '0.375rem',
    })
  };

  return (
    <MDBContainer fluid className="p-4 content-area flex-grow-1">
      <h2>{t("editapp")}</h2>

      <NativeInput
        label={t("appname")}
        name="name"
        value={form.name}
        onChange={handleFormChange}
        className="mb-3"
        required
      />
      <NativeInput
        label={t("description")}
        name="description"
        value={form.description}
        onChange={handleFormChange}
        className="mb-3"
      />
      <NativeInput
        label={`${t("Arabic Name")} *`}
        name="arabicName"
        value={form.arabicName}
        onChange={handleFormChange}
        className="mb-3"
        required
        maxLength={160}
        placeholder={t("Enter Arabic name (Arabic characters only, max 160 chars)")}
      />
      <NativeInput
        label={`${t("Arabic Description")} *`}
        name="arabicDescription"
        value={form.arabicDescription}
        onChange={handleFormChange}
        className="mb-3"
        required
        maxLength={160}
        placeholder={t("Enter Arabic description (Arabic characters only, max 160 chars)")}
      />
      <NativeInput
        label={t("logourl")}
        name="logo"
        value={form.logo}
        onChange={handleFormChange}
        className="mb-3"
      />

      {!isOther && (
        <>
          <NativeInput
            label={t("apiurl")}
            name="apiUrl"
            value={form.apiUrl}
            onChange={handleFormChange}
            className="mb-3"
            required
          />

          <label className="form-label">{t("integrationmethod")}</label>
          <select
            name="integrationMethod"
            value={form.integrationMethod}
            onChange={handleFormChange}
            className="form-select mb-3"
            required
          >
            <option value="API_KEY">{t("API Key")}</option>
            <option value="BEARER_TOKEN">{t("Bearer Token")}</option>
            <option value="NONE">{t("None")}</option>
          </select>

          <label className="form-label">{t("integrationtype")}</label>
          <select
            name="integrationType"
            value={form.integrationType}
            onChange={handleFormChange}
            className="form-select mb-3"
            required
          >
            <option value="DIRECT">{t("Direct")}</option>
            <option value="AUTHORIZATION_CODE">
              {t("Authorization Code")}
            </option>
          </select>

          <NativeInput
            label={t("credentialstemplate")}
            name="credentialsTemplate"
            value={form.credentialsTemplate}
            onChange={handleFormChange}
            className="mb-3"
            placeholder='{ "clientId": "string", "clientSecret": "string" }'
          />

          {appType === "CHEFZ" && (
            <>
              <NativeInput
                label={t("Provider Key")}
                name="providerKey"
                value={form.providerKey}
                onChange={handleFormChange}
                className="mb-3"
                required
              />
              <NativeInput
                label={t("Webhook Token")}
                name="webhookToken"
                value={form.webhookToken}
                onChange={handleFormChange}
                className="mb-3"
                required
              />
            </>
          )}

          {appType === "KEETA" && (
            <>
              <NativeInput
                label={t("App ID")}
                name="appId"
                value={form.appId}
                onChange={handleFormChange}
                className="mb-3"
                required
              />
              <NativeInput
                label={t("Authorization URL")}
                name="authorizationUrl"
                value={form.authorizationUrl}
                onChange={handleFormChange}
                className="mb-3"
                required
              />
            </>
          )}
        </>
      )}

      <label className="form-label">{t("status")}</label>
      <select
        name="status"
        value={form.status}
        onChange={handleFormChange}
        className="form-select mb-3"
        required
      >
        {<option value="Active">{t("active")}</option>}
        <option value="Coming Soon">{t("comingsoon")}</option>
        <option value="Pending">{t("pending")}</option>
        <option value="Inactive">{t("inactive")}</option>
        <option value="QA Testing">QA Testing</option>
      </select>

      {form.status === "QA Testing" && (
        <div className="mb-3">
          <label className="form-label">
            {t("Select Users for QA Testing")}
            {form.allowedUser.length > 0 && (
              <Tag variant="info" className="ms-2">
                {form.allowedUser.length} {t("selected")}
              </Tag>
            )}
          </label>
          
          {loadingUsers ? (
            <div className="text-muted">{t("Loading users...")}</div>
          ) : users.length === 0 ? (
            <div className="text-muted">{t("No users available")}</div>
          ) : (
            <div className="multi-select-container position-relative" ref={containerRef}>
              <div 
                className="form-control cursor-pointer d-flex justify-content-between align-items-center"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{ 
                  cursor: 'pointer',
                  minHeight: '38px',
                  backgroundColor: '#fff'
                }}
              >
                <span>
                  {form.allowedUser.length === 0 
                    ? t("Select users...")
                    : `${form.allowedUser.length} ${t("user(s) selected")}`}
                </span>
                <span style={{ 
                  transform: dropdownOpen 
                    ? (dropdownPosition === 'top' ? 'rotate(0deg)' : 'rotate(180deg)') 
                    : (dropdownPosition === 'top' ? 'rotate(180deg)' : 'rotate(0deg)'),
                  transition: 'transform 0.2s' 
                }}>
                  ▼
                </span>
              </div>

              {dropdownOpen && (
                <div 
                  ref={dropdownRef}
                  style={dropdownStyles}
                >
                  <div className="sticky-top bg-white p-2 border-bottom">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={t("Search users...")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  {filteredUsers.length > 0 && (
                    <div 
                      className="px-3 py-2 border-bottom hover-bg-light cursor-pointer d-flex align-items-center sticky-top bg-light"
                      onClick={handleSelectAll}
                      style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={form.allowedUser.length === filteredUsers.length && filteredUsers.length > 0}
                        readOnly
                        style={{ cursor: 'pointer' }}
                      />
                      <strong>{t("Select All")}</strong>
                    </div>
                  )}

                  <div style={{ maxHeight: '165px', overflowY: 'auto' }}>
                    {filteredUsers.map((user) => {
                      const userId = user._id || user.id;
                      const isSelected = form.allowedUser.includes(userId);
                      
                      return (
                        <div
                          key={userId}
                          className="px-3 py-2 hover-bg-light cursor-pointer d-flex align-items-center"
                          onClick={() => handleUserToggle(userId)}
                          style={{ 
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#e7f3ff' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input me-2"
                            checked={isSelected}
                            readOnly
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '0.9rem' }}>{getUserDisplay(user)}</span>
                        </div>
                      );
                    })}

                    {filteredUsers.length === 0 && (
                      <div className="px-3 py-2 text-muted">
                        {t("No users found")}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Button color="primary" onClick={handleSubmit} className="mt-3">
        {t("savechanges")}
      </Button>
    </MDBContainer>
  );
}

export default AdminEditApp;